app:
  name: Financing
  next: Next
  advance: Advance
  advance_and_update: Update and advance
  share: Share
  whatsapp: WhatsApp
  send: Send
  save: Save
  save_equipment_changes: Save changes
  cancel: Cancel
  edit_equipment: Edit
  cancel_equipment_edit: Cancel change
  cancel_analysis: Cancel analysis
  backList: Back to list
  alert:
    partner:
      title: 'Viewing projects as: | Viewing project as: '
      statement_title: 'Viewing statement as: '
      textButton: Admin View
      description: >-
        Here you will find projects of just the partner above. To see all
        projects click on the button on the side. | To return to the admin view,
        click on the button on the side.
      statement_description: Here you will find the statement of just the partner above.
    temporary_instability:
      title: Temporary instability
      description: Our platforms may experience slowness or failures due to an instability. We are monitoring the situation.
  back: Go back
  send_biometry: Send biometrics
  create_new_simulation: Analyze credit
  send_data: Submit data
  save_equipments: Save equipment
  check_payment_mode_information: Check information
  confirm: Confirm
  send_for_analysis: Send for analysis
header:
  welcome: Welcome
  exit: Exit
  project:
    title: Financing
    power: Power
    capacity: Capacity
    system: System
    system_types:
      without_battery: Complete
      with_battery: Complete with battery
      only_battery: Battery only
    combo: Easy Combo
    combo_connect: Connect Combo
    steps:
      formalization: Proposal
      simulation: Simulation
      installation: Installation
    expired: Expired
    turbo:
      tag: Turbo project
      alert:
        title_before: This
        title_emphasis: specific project
        title_after: is turbo
        description: >-
          Simplified documentation review: fewer requirements and more direct approval.
          Continue with formalization.
    only_battery:
      alert:
        title: 'Attention: inverter and battery compatibility'
        description: >-
          The compatibility between the chosen inverter and battery is the partner's responsibility.
          Different models, even from the same manufacturer, may not be compatible with each other.
          Always check the datasheet and, if in doubt, consult the manufacturer before closing the project.
  bnpl:
    steps:
      simulation: Simulation
      formalization: Proposal
      external: External
    status:
      IN_PROGRESS:
        type: warning
        text: In progress
      EXPIRED:
        type: neutral
        text: Expired
      DONE:
        type: positive
        text: Done
  impersonate:
    modal:
      title: View as a partner
      subtitle: Search by the Name or CNPJ of the partner you want to view
      input_label: Partner name or CNPJ
      button: View
      empty_state:
        title: Partner not found
        description: >-
          It seems that the partner you are looking for does not exist. Please
          check the data and try again.
    error:
      title: Error to view partner
      description: >-
        It was not possible to view the partner. Please check the data and try
        again.
  simulation:
    status:
      reproved:
        text: Reproved
      approved:
        text: Approved
  search:
    placeholder: Name, CPF/CNPJ or Project ID
    loading:
      title: Loading results
      text: Searching projects...
    empty:
      type_to_search: Type to search
      press_enter: Press Enter to search
      no_results: No results found
    labels:
      project_name: Project name
      id: ID
      step: Step
      more_projects: More Projects
    project_fallback: Project
    status:
      approved: Approved
      in_progress: In progress
      under_analysis: Under analysis
      reproved: Reproved
      expired: Expired
      canceled: Canceled
      to_review: Review
      waiting_customer: Waiting customer
    step:
      simulation: Simulation
      equipment: Equipment
      customer_data: Customer data
      biometry_and_contract: Biometry and contract
      payment_model: Payment model
      installation: Installation
home:
  title: Projects
  subtitle: All your projects in one place
  newSimulation: New simulation
  banner:
    title: New formalization platform!
    subtitle: After completing the simulation, formalize faster in this new experience
overview:
  title: Overview
  intro_modal:
    title: What’s new in the Financing Portal
    description: We have a <strong>new experience</strong> designed to help you spend less time searching for information and more time closing deals
    description_2: With this new experience, you can manage your projects more efficiently and make better use of your available time.
    bullets:
      navigation: <strong>Improved navigation</strong> and <strong>more space</strong> to manage your projects.
      search: <strong>Project search</strong> always at hand.
      projects_panel: <strong>New Overview screen</strong>, with projects in progress panel and direct links to the filtered list.
      metrics: In Overview, better visibility of your <strong>payment model</strong> and your <strong>customer portfolio health</strong>.
      projects_in_progress: Improved navigation and more space to manage your projects;
      sales_funnel: Project search always at hand;
      click_to_filter: Projects in progress panel with easy access to projects in each status;
      wallet_health: Better visibility into your customer portfolio health.
      payment_model: Better visibility into your payment model.
    actions:
      start_tour: See what’s new
      view_all: See all updates
      view_this_screen: See updates on this screen
      skip_tour: Skip
  projects_in_progress:
    title: Projects in progress
    view_all: View all
    total: Total
    status:
      in_progress: In progress
      to_retrieve: To retrieve
    categories:
      simulations: Simulations
      proposals: Proposals
      installations: Installations
    breakdowns:
      approved_simulations: Approved simulations
      reassessments: Reassessments
      equipment: Equipment
      customer_data: Customer data
      biometry_and_contract: Biometry and contract
      payment_model: Payment model
      installations_in_progress: Installations in progress
    mobile_labels:
      approved: Approved
      in_progress: In progress
      data: Data
      biometry: Biometry
      model: Model
    last_updated:
      updating: Updating...
      error: Error loading data
      now: Updated just now
      less_than_minute: Updated less than a minute ago
      minutes: Updated {count} minute ago | Updated {count} minutes ago
      hours: Updated {count} hour ago | Updated {count} hours ago
      days: Updated {count} day ago | Updated {count} days ago
    empty:
      title: Start a new project
      subtitle_line1: You don't have any projects yet.
      subtitle_line2: Run a simulation and take advantage of financing with Solfácil.
      new_simulation: New simulation
    not_impersonate:
      title: View a partner's projects
      subtitle: To view projects, select the company to enter partner view mode.
      view_as_partner: View as partner
  notifications:
    title: Notifications
    empty:
      title: Stay tuned to the bell
      subtitle: Here you can follow alerts and news about your financing and projects.
  wallet_health:
    title: Your Wallet Health
    download_button: Client list
    image_alt: Wallet health illustration
    intro:
      description1_prefix: 'Having a client portfolio that '
      description1_highlight1: pays financing installments on time
      description1_middle: ' helps you unlock more '
      description1_highlight2: benefits and opportunities
      description1_suffix: ' when financing with Solfacil.'
      description2: Review your portfolio periodically.
    arrears:
      title: Clients in arrears
      description: Check the list of clients who currently have overdue financing installments.
      button: Download list (.csv)
    descriptions:
      bom:
        title: Congratulations! Your indicators are healthy.
        subtitle: You already have the best installment for your level.
      regular: Improve your rates by adjusting the open installments of some of your clients.
      ruim: You have several clients who are not paying their installments. Adjust your default rates to improve the rates you offer to your clients.
      na: You do not yet have wallet health information for your client portfolio in the last year.
      full: Congratulations, you have no clients with pending installments.
    info:
      full:
        title: You have no clients with pending installments.
      improve:
        title: 'How to keep your wallet healthy:'
        tip1: Check if there was any problem with the installation.
        tip2: Check if the project is generating the expected savings.
        tip3: Contact your clients and understand the reason for the default.
        tip4: Check the connectivity of installed Amperas
    how_works:
      title: How does it work?
      description1: With Solfácil Mais, the higher your level, the better the installments you can offer to your clients in financing.
      description2: In addition to your level in the program, we also monitor the default rate of your client portfolio. The higher your level and the more compliant your clients, the better your installments will be!
    not_impersonate:
      title: View a partner's wallet health
      description: To do this, enter partner viewing mode.
      button: View with partner
  payment_model:
    title: Payment model
    not_impersonate:
      title: View a partner's payment model
      description: To do this, enter partner viewing mode.
      button: View with partner
  onboarding:
    step1:
      title: New Simulation
      description: Click here to create a new financing simulation for your clients.
    step2:
      title: Projects in Progress
      description: Track all your projects in progress, from simulations to completed installations.
    step3:
      title: Wallet Health
      description: Monitor your client portfolio health and see how to improve your financing rates.
    step4:
      title: Payment Model
      description: See what your payment model is based on your installation performance.
  banner:
    dispara_solfacil_alt: Dispara Solfácil campaign banner — R$ 50,000 prepaid card
    boleto_em_dia_alt: Paid Bills, Lucky Streak banner
    golaco_solfacil_alt: Golaco Solfácil banner
  news:
    title: More advantages for you and your clients
    combo_conecte:
      title: Combo Conecte
      description:
        prefix: Choose
        highlight: Combo Conecte
        suffix: and ensure greater financing approval and more advantages.
      button_text: View Combo Conecte
    combo_facil:
      title: Combo Fácil
      description:
        prefix: With
        highlight: Combo Fácil
        suffix: you take advantage of Solfácil store offers and have more advantages when financing.
      button_text: View Combo Fácil
    seguros:
      title: Insurance
      description:
        prefix: Insurance for
        highlight: Various Risks
        middle: and
        highlight2: Loss of Income
        suffix: Complete protection for your customer.
      button_text: View Insurance
resumeWidget:
  simulationSummary: Simulation summary
  summary: Summary
  close: Close
  gracePeriod: Grace period
  month: |-
    month |
    months
  financedValue: Financed value
  downPayment: Down payment
  totalProjectValue: Total project value
  cet: |-
    CET |
    CET black
  interestRate: Interest rate
  installment: Installment
  financing: Financing
  opportunities:
    title: Your gains
    commission: Commission
    insuranceCommission: Insurance Commission
simulation:
  next: View installments
  load_again: Load again
  founded: Simulations found
  golist: Go to list
  list: Simulations
  subtitles: Access your saved simulations
  new: New simulation
  project_type: Financing lines
  cpf_use_terms: >-
    I declare that my client has authorized the consultation of their CPF or
    CNPJ in credit protection agencies.
  continue_with_value: Continue with approved amount
  financing_options: Financing options
  details:
    info_title: Simulation data for visualization purposes only.
    info_description: >-
      You will be able to review your simulation and change data during the
      contract signing stage.
  conclude: Conclude simulation
  info_risk:
    necessary_install_pending_project_title: |-
      You have pending installations.
      Conclude your outstanding facilities to formalize this project.
    necessary_install_pending_project_description: >-
      To formalize this financing it is necessary to complete its pending
      facilities.
  bnpl:
    solfacilInstallment: Solfacil Installment
    identity: id
    installment_title: |-
      Installment |
      Installments
    conditions_installment: >-
      Check out our options for the one that best fits your client's profile. It
      is possible to simulate the value of the project kit, and define it in the
      next stage, proposal.
    project_value: Project value
    due_date: Maturity
    invoice_due_date: Invoice due date
    kit_value_helper: Updated after kit selection
    kit_value: Kit value
    number_order: N° order
    service_value_helper: Project value minus kit and fee
    service_value: Service value
    installment_amount: Installment value
    initial_amount: Prohibited
    receive_amount: Will receive
    customer_pays_fee: Installment with fixed service
    store_pays_fee: Installment with fixed project
    customer_pays_fee_description: You pass on the fee to your client, increasing the value of the project.
    store_pays_fee_description: You pay the fee, maintaining the value of the project.
    id:
      summary:
        title:
          summary: Summary
          service: Fixed service
        installment:
          title: Installment
          prohibited: Prohibited
          cet: CET
          am: a.m.
        enterprise:
          title: For your company
          total: Total project value
          kit: Store Kit
          tax: Advance fee
          value: Amount to receive
        client:
          title: For your client
          total: Total project value
          prohibited: Prohibited
          installments: Installments
          of: of
  modal:
    title: New proposal platform!
    altImg: Funding Screen
    info:
      proposal:
        title: Send your proposal in up to 2 minutes!
        description: Fill out everything at once and wait for our analysis.
      projects:
        title: Make your projects whenever and wherever you want!
        description: 'Cell phone, computer or tablet: you choose how and where.'
      payments:
        title: Real-time payments
        description: After approval, payments 24 hours a day, 7 days a week.
    button:
      old: Continue with the old platform
      new: Continue with the new platform!
  notFound:
    title: Simulation not found
    subtitle: >-
      It seems that the simulation you are looking for does not exist. Please
      check the data and try again.
  errorProject:
    title: Error to load list
    subtitle: >-
      It seems that there was an error loading the list of simulations. Please
      try again later.
  status_filter:
    done: Approved
    pending: Pending
    reproved: Reproved
  result:
    bnpl:
      modals:
        important:
          description: >-
            You will be held responsible if the customer defaults. Solfácil may
            request that you pay the installment amounts in full, and may also
            enter your company's details in the Credit Protection Bodies.
        interest:
          description: >-
            Buying the kit at Solfácil store, you have more this option to close
            deal!
          title: How does the installment the installment Solfácil?
      alerts:
        responsibility:
          description: >-
            Does the customer know that, even without credit, you pay in
            installments, assuming the risk? It's similar here, check
            conditions.
      will_be_pre_approved: Check the conditions that would be pre-approved
      approved_with_restrictions: Approved, with restrictions
      pre_approved_value: Pre-approved amount
      max_installments: Installments in up to
      buttons:
        textFollowInstallment: Installment
      customerRejectedGuarantor:
        title: REPROVED CUSTOMER
        description:
          title: There are two actions that you can take
          actions[0]: >-
            1. Consult credit protection agencies and see if this CPF or CNPJ is
            regular.
          actions[1]: 2. Refares the analysis and add another guarantor.
      subtitle: Installment without compromising your customer's card credit
      title: Solfácil
      customerRejected:
        altImg: Image representing the customer being disapproved
        onlyBatteryLimit:
          title: Battery financing unavailable at the moment
          description: >-
            It is not possible to proceed with battery-only financing for this
            project right now. New simulations will be available from next
            month.
    financing:
      down_payment: Input (for you)
      requested_value: Requested value
      installment_value: Installment value
      best_condition: Best condition
      combo_easy_text: Special conditions with easy combo
      combo_connect_text: Special conditions with connect combo
      combo_conecte_only_text: Combo Conecte Exclusivity
      combo_conecte_only:
        button_text: Finance with Combo Conecte
        know_more: Understand Combo Conecte
        consult_inverters:
          prefix: '*Check '
          link_text: compatible inverters
          suffix: ' with Combo Conecte'
    modals:
      combo_connect:
        title: Financing with Combo Connect
        button_ok: Close
        tabs:
          benefits: Benefits and rules
          inverters: Compatible inverters
        benefits:
          description: >-
            Financing and buying a kit with Solfácil, your customer pays less
            and you sell more. With Combo Conecte you have advantages like:
          title: Combo Conecte Benefits
          items:
            one: Easy credit approval
            two: Higher margins in the transaction
            three: Competitive prices at Solfácil Store
            four: Lower installments for your customer
            five: More points in Solfácil Mais
        important:
          title: It is important to know that
          items:
            one:
              text: Combo Conecte requires the purchase of
              highlight: compatible inverters.
            two:
              text: The installation site must have
              highlight: Wi-Fi internet available for connection with the inverter.
            three:
              text_before: Inverters in Combo Conecte mode allow
              highlight: remote generation management
              text_after: ', according to financing rules or in case of prolonged Wi-Fi disconnection.'
        inverters:
          description: >-
            Check the inverters compatible with Combo Conecte and check availability at Solfácil Store.
          store_button: Go to Store
          alert:
            description: >-
              Microinverters are not available in Combo Conecte.
          list:
            brand: Brand
            name: Model
            power: Power
          power: Power
          empty:
            title: No inverters found
            subtitle: We did not find inverters compatible with Combo Conecte for this project.
    data:
      down_payment: Prohibited
      systemPower: System Power (KWp)
      batteryCapacity: Battery capacity (kWh)
  return_formalization: Go to proposal
  ampera:
    lack_of_stock:
      text1: >-
        Since this is a second financing, a new Ampera is not required for this
        installation.
      text2: >-
        The Ampera device is no longer available, but don't worry: your
        installment already has the best value + discount!
    textLink: More details
    description: >-
      Optimize your after-sales with monitoring and receive discounts on
      installments.
  newPage:
    title: Customer Type
    description: Define your customer's profile for this project
    pf:
      title: Individual (PF)
      description: Credit line for residences
      financing:
        title: Financing up to {value}
        description: >-
          In up to {maxFinancingInstallments} installments - Insurance -
          {period}
    pj:
      title: Legal Entity (PJ)
      description: Credit line for companies
      financing:
        title: Financing up to {value}
        description: >-
          In up to {maxFinancingInstallments} installments - Insurance -
          {period}
    client_data:
      title: Project data
      description: Special credit conditions for your customer in a few seconds.
      form:
        document_pf:
          title: Customer CPF*
        document_pj:
          title: Customer CNPJ*
          majoritary:
            title: CPF of the majority shareholder*
        value:
          title: Total project value*
          placeholder: Enter the project value
        down_payment:
          title: Prohibited
          placeholder: Enter the entry value
        power:
          title: System power*
          placeholder: Enter the power value
          system_power_error_422: Decrease project value or increase power to continue
          battery_capacity_title: Battery capacity*
          battery_capacity_placeholder: Enter the capacity value
          battery_capacity_range_error: Battery capacity must be between 1 kWh and 999 kWh
          battery_capacity_error_422: Decrease project value or increase battery capacity to continue
        error:
          title: An error occurred when trying to create the project.
        bill_value:
          title: Current amount of the electricity bill of all consumer units*
          placeholder: Enter the account value
          helperText: >-
            The amount will be validated by sending the invoices of the consumer
            units
          title_modal: How do multiple light bills work?
          description_modal: >-
            The project can be composed of more than one consumer unit to
            benefit from the photovoltaic system, provided it meets the
            following rules:
          description_modal_item1: Accounts need to be all of the same energy concessionaire;
          description_modal_item2: >-
            It is necessary to prove family bond between the electric bill
            holders and the end customer;
          description_modal_item3: >-
            The date of issuance of the electricity bill may have a maximum of 3
            months;
          description_modal_item4: >-
            The electricity bill to be filled in the simulation should be the
            current total consumption of consumer units.
    empty:
      title: Error when loading financing types
      subtitle: >-
        An error occurred when trying to load the types of financing (PF and
        PJ).

        Please try again later.
revision:
  title: Simulation review
  subtitle: >-
    Review and change all simulation data, including values, as long as they
    comply with our policies.
  subtitle_complement: Otherwise, it will be necessary to perform a new simulation.
buttons:
  back: Back
  discart: Discard change
  edit_save: Save changes
  go_financing: Go to financing
  go_new_page: Redo analysis
loading:
  messages: >-
    We're almost there, harnessing solar energy to power our calculations.
    Sunbeams are arriving, charging with the strength of the sun.
list:
  days: '- | {count} day | {count} days'
installments:
  with_combo: Installments with Easy Combo
  with_combo_description: >-
    Affordable installment for your customer. The kit must be purchased at the Solfácil Store.
  with_combo_comission: Installments with Easy Combo and commission
  with_combo_comission_description: >-
    Best value for money for you and your client. The kit must be purchased at
    the Solfácil Store.
  with_combo_conecte: Installments with Connect Combo
  with_combo_conecte_description: >-
    An even more affordable installment for your customer. The kit must be purchased at the Solfácil Store.
  combo_conecte_label: Connect Combo
  combo_facil_label: Easy Combo
  novelty_label: New
  combo_conecte_tooltip: The Combo Conecte requires continuous connection of inverters to the internet, enabling Solfácil to offer even more advantageous financing conditions. In this model, in case of default or internet disconnection, the inverter is turned off. Check the compatible models in the equipment step.
  ampera_label: Ampera
  insurance_label: Insurance
  standard_label: Standard
  without_combo: Installments without Easy Combo
  without_combo_description: >-
    Most expensive option for your client. The kit can be purchased outside the
    Solfácil Store.
  banner_combo_offer:
    start: Special offer for you!
    middle1: Get
    middle2: 10% discount
    end: on the value of the kit products when choosing one of the Combo installments.
form:
  energy_concessionaire: Energy utility
  alert_required_fields: Please fill in all required fields.
  quantity: Quantity
  file_required: File submission required.
  invalid_file_type: |
    Invalid file type. Accepted file types are: {types}
  required: Required field
  past_date_required: Past date required
  cpf_duplicate: The CPF provided is already being used by another registration.
  error: Registration not completed, try again shortly.
  informativeCepText: Problems with the zip code?
  cepModal:
    title: Problems with the zip code?
    titleButton: Close
    informative:
      title: >-
        If you have problems with the CEP, watch the video or follow the steps
        below:
      item1: '1st Access the website Search postal code:'
      item2: >-
        2nd In the first field, write the name of the street, city and Federal
        Unit (abbreviated).

        In the second field it is not necessary to make changes.
      item3: 3rd Fill in the code shown in the image, and click the “Search” button.
      item4: >-
        4th On the next screen, the zip code that needs to be entered on the
        platform will appear.
      item5: 5th If your doubts persist, watch the video above for more information
  cepInfo: >-
    The zip code must be the same as that used for postal deliveries, even if a
    different one appears on the electricity bill.

    If the question persists, click on the button below "Problems with the zip
    code?".
  cancelAnalysis:
    confirm: Confirm cancellation
    back: Back
    title: Cancel the analysis?
    message: |-
      You are about to cancel the analysis of the information you entered.
      This will interrupt the process and a new submission will be required.
      Are you sure you want to continue?
    successToast: Analysis canceled successfully.
    errorToastDescription: Unable to cancel analysis.
  toast:
    success: Registration completed successfully!
  repeated_document: The CPF cannot be the same as the customer's.
  optional_placeholder: Optional
  correction_letter: Correction letter
  email:
    required: ''
    invalid: ''
  email-invalid: Invalid Email
  email-required: E-mail is mandatory
  service_invoice: Invoice full of the project issued by your company*
  project_invoice: Project invoice*
  equipment_invoice: Equipment invoice*
  guarantor:
    placeholder: CPF EVALIST
    button: Include guarantor
    valid_cpf: Enter a valid CPF
    error_toast_default: |-
      Error when including a guarantor.
      Try again.
    error_toast_guarantor_same_document: CPF of the guarantor cannot be the same as the customer
  cpf_required: CPF is mandatory
  correnction_letter:
    helper_text: Add the correction letter to adjust your project
  max: Maximum of {max} characters
  invalid_cep: Invalid zip code
  invalid_cnpj: Enter a valid CNPJ
  invalid_cpf: Enter a valid CPF
  payment_model:
    correction_letter_approved: Correction letter approved
    invoice_approved: Invoice approved
company:
  energy_bill_subtitle: |-
    We will request the electricity bill to be sent at the documentation stage.
    If it is not in the customer's name, simply indicate the holder's CPF.
  biometry:
    modal_sending:
      due_date:
        title: Installment due dates
        description: Remind your customer of these dates and change them if needed.
        first_due_date: First installment due date
      due_day: Due day
      majority_info:
        title: Majority shareholder contact information
        description: The biometry collection request will be sent to the majority shareholder using these information.
      first_legal_info:
        title: First legal representative contact information
        description: The biometry collection request will be sent to the first legal representative using these information.
      second_legal_info:
        title: Second legal representative contact information
        description: The biometry collection request will be sent to the second legal representative using these information.
      guarantor_info:
        title: Guarantor contact information
        description: The biometry collection request will be sent to the guarantor using these information.
      title: Please confirm the following information
      helper_text:
        edit_suggestion:
          first_line: 'Don''t forget: You have'
          second_line: '{count} tries'
          last_line: to update the customer's data.
        no_changes:
          first_line: 'Don''t forget: You'
          second_line: don't have any changes
          last_line: to update the customer's data.
      send: Send biometrics and contract
      confirm_edit: Confirm change
      edit: Edit data
      cancel: Cancel
      form:
        email: E-mail
        phone: Phone
        required: Required field
        email_error: Please enter a valid email
        phone_error: Please enter a valid mobile phone number
        submit: Confirm edit
        majority:
          email: Majority shareholder e-mail
          phone: Majority shareholder phone
        legal:
          email: Legal representative e-mail
          phone: Legal representative phone
        legal_1:
          email: 1st legal representative e-mail
          phone: 1st legal representative phone
        legal_2:
          email: 2nd legal representative e-mail
          phone: 2nd legal representative phone
customer:
  equipment_edit_modal:
    title: Discard change?
    description: By leaving the equipment editing, the changes made will be discarded.
    confirm: Discard
    cancel: Back to editing
  equipment_success_update: Equipment successfully updated
  equipment_success_save: Equipment successfully saved
  equipment_error_save: Error saving equipment. Please try again later.
  distributor_installation_guard:
    mismatch:
      title: Mismatch between utility and address
      description: The selected utility company does not match the installation address state.
  equipment_step:
    subtitle: Enter the equipment that will be purchased and financed in this project.
    section_title: Kit to be financed
    loading: Loading equipment information
    deadline_alert:
      title: Keep an eye on the deadlines and avoid blocks
      description: 'You have until {date} to complete the Equipment and Receipt steps.'
    origin:
      label: Where will the equipment be purchased?
      solfacil: Solfácil
      other: Other distributor
      combo_info: For Combo projects, equipment must be purchased from Solfácil.
    acquisition:
      label: Kit acquisition method
      store: Store order
      container: Container balance
      container_info: Select Container balance if you want to use equipment from your stock at Solfácil.
      options:
        mirror: Store order
        selected: Other distributor
        container: Container balance
        own_equipment: Own stock
    store_order_reason_alert:
      title: Pending issue!
      expired_description: Your order is canceled or expired. It must be in awaiting payment status.
      not_payment_description: Your order is not in an expected status for this step. It must be in awaiting payment status.
    store_order:
      label: Store order
      placeholder: Select the order placed in the Solfácil store...
      no_order: I haven't placed the order yet
      view_order: View my order
      expires_at: This order expires on {date}.
      expired_at: This order expired on {date}. Choose another order.
      option: 'Order #{id} - R$ {value}'
      errors:
        invalid_for_combo_conecte: Order is not compatible with Combo Conecte
        size_incompatible: The selected order is incompatible with the project size.
        used_another: This order is already linked to another project.
        invalid_for_project_title: The selected order is not valid for the project.
        invalid_power: Invalid power information for the hardware.
        invalid_battery: Invalid battery information for the hardware.
        order_expired: The selected order is canceled or expired. Choose another order in awaiting payment status.
        order_not_payment: Your order is not in an expected status for this step. Choose another order in awaiting payment status.
    project_details:
      phase: Installation site phase
      structure: Structure
      structure_placeholder: Select...
      system_power: System power
      battery_capacity: Battery capacity
      system_type: System type
      system_type_without_battery: Without battery
      system_type_with_battery: With battery
      system_type_only_battery: Battery only
    actions:
      save_continue: Save and continue
      verify: Verify information
      verified: Equipment verified
    unit_kwp: '{value} kWp'
  header:
    message:
      error: An error occurred while obtaining the proposal status.
    steps:
      simulation: Simulation
      installation: Installation
      formalization: Proposal
    tags:
      expired: Expired
      canceled: Canceled
      reproved: Failed
      approved: Approved
      pendency: Pending
      pending: In progress
      under_analysis: Under review
      send_contract: Send contract
      sent: Pending customer
      submitted: Sent
  canceled:
    title: Project canceled
    description: >-
      The client requested the cancellation of this project and the transfer of
      values will not be carried out. If it has already been done, it will be
      reversed. In the case of Combo Fácil, the kit order will also be
      cancelled.
    risk_title: We cannot follow with this financing
    risk_description: >-
      The proposal was rejected in the data validation stage due to
      inconsistencies in the information presented, according to our internal
      policy.
    internal_policy_title: Definitive rejection
    internal_policy_description: Customer in disagreement with internal policy.
    requested_by_customer_title: Project Canceled
    requested_by_customer_description: >-
      The customer requested the cancellation of this project and the transfer of values will not be carried out. If it has already been done, it will be reversed. In the case of Combo Fácil, the kit order will also be cancelled.
  new: Proposal
  receipt_model_title: Receipt
  receipt_model_subtitle_combo: >-
    Confirm your receipt model so we can make the transfers to your account.
  receipt_model_subtitle_default: >-
    Confirm the details of your receipt model and submit the invoices.
  receipt_model_subtitle_default_complement: >-
    After the review, track the amounts to be received and the transfer receipts.
  invoice_deadline_alert:
    title: >-
      You have until {date} to submit the invoices and confirm your receipt
      information.
    title_no_date: >-
      Be sure to submit the invoices and confirm your receipt information within
      the deadline.
    text: >-
      Keep an eye on the deadline. If it is not met, your company may be blocked
      for new projects.
  descriptionUnableContinuefinancing: >-
    Some customer data may need adjustment.

    Please check that the contact and address belong to the customer, not the
    seller or a relative.

    After this confirmation, create a new project and send it for analysis.

    Please note that, even so, the system may automatically disapprove the
    proposal in some cases, in accordance with our internal policy.
  document_data_subtitle: >-
    Please attach the following documents from your client below. Preferably in
    .pdf format.
  biometry:
    formalization_started: Formalization started success
    cancel_success: The sent biometry canceled successfully
    step_subtitle: Confirm the data below and then ask your client to complete the biometrics and sign the contract.
    confirm_and_advance: Confirm and continue
    confirmation:
      title: Project information verification
      description: Confirm the data below before sending the documents for the client's signature.
      status_in_progress: In progress
      status_approved: Approved
    steps_box:
      title: Steps to be completed by your client
      description: After the confirmation above, follow your client through the steps below.
    compatibility:
      title: Inverter compatibility
      consult_before: Check the list of
      consult_link: inverters compatible with Combo Conecte
      consult_after: Then place an order at the store to validate your choice.
      order_label: Store order
      order_placeholder: Select the order
      order_option: 'Order #{id} - R$ {value}'
      go_to_store: Go to store
      see_inverters: See compatible inverters
      change_later: You can change to another order later, if needed.
      errors:
        incompatible: This order is not compatible with Combo Conecte. Select another order.
        expired: This order has expired. Select a new order.
        generic: We couldn't validate the order. Select another order.
    edit_modal:
      title: Change client data
      warning_before: This change can be made
      warning_times: '{count} times'
      warning_after: When you save, the project will go through a new analysis.
      email_label: Client e-mail
      phone_label: Client phone
      cancel: Cancel
      save: Save change
    catch:
      data_error_title: Biometry data error
      has_been_cancelled: Contract has been cancelled, wait {minutes} minute(s) to cancel again"
    alert:
      reproved_biometry:
        title: Customer biometry not approved
        subtitle: >-
          The customer failed this stage due to inconsistencies and This project
          cannot be formalized. This type of reproach is irreversible.
      reproved:
        title: Its not possible follow with the financing
        subtitle: The customer already has a project in the formalization process.
      status_reason:
        reproved:
          title: Formalization rejected
        cancelled:
          title: Formalization cancelled
        blocklist_pending:
          title: Formalization pending
    update_error: >-
      Due to our internal policies, it is not possible to proceed with the
      change.
    know_errors:
      BIOMETRY_ALREADY_DONE: >-
        It was not possible to update because the biometrics have already been
        done
      FORMALIZATION_COMPLETED: >-
        It was not possible to update because the formalization has already been
        completed
      INVALID_STATUS: It was not possible to update because the status is invalid
      MAX_TRIES: >-
        It was not possible to update because the maximum number of attempts has
        been reached
      MATRIX_RESTRICTIONS: >-
        Due to our internal policies, it is not possible to proceed with the
        change.
      SAME_EMAIL_PHONE: >-
        It was not possible to update because the email and phone number are the
        same
    update_success: Contact details have been changed successfully
    buttons:
      resend_biometry: Resend biometry
      resend_contract: Resend contract
    resend:
      success:
        title: Resend completed
        biometry_description: Biometry resent successfully
        contract_description: Contract resent successfully
      error:
        title: Resend error
        biometry_description: Error resending biometry. Please try again.
        contract_description: Error resending contract. Please try again.
      wait: Please wait... {seconds}s
    modal_sending:
      contact_info:
        title: Customer contact information
        description: Where your customer will receive the instructions and the contract.
      guarantor_info:
        title: Guarantor contact information
        description: The biometry collection request will be sent to the guarantor using these information.
      title: Please confirm the following information
      helper_text:
        edit_suggestion:
          first_line: This change can be made
          second_line: '{count} times.'
          last_line: By saving, the project will go through a new analysis.
        no_changes:
          first_line: 'Don''t forget: You'
          second_line: don't have any changes
          last_line: to update the customer's data.
      send: Confirm and advance
      confirm_edit: Confirm change
      edit: Change
      cancel: Cancel
      back: Back
      form:
        required: Required field
        email: E-mail
        email_error: Please enter a valid email
        phone: Phone
        phone_error: Please enter a valid mobile phone number
        submit: Confirm edit
        email_guarantor: Email of the guarantor
    date:
      last_submitted_at: 'Last Shipping:'
      not_started: (not started)
      completed_at: 'Completed in:'
      in_progress: (in progress)
    button:
      check-complete-biometry: Confirm biometrics
  terms_acceptance:
    title: Terms of acceptance of Combo Connect
    subtitle: >-
      Your customer will receive a WhatsApp and email with instructions to sign the acceptance terms in which they confirm they are aware of the need for internet access via WiFi network for the inverter to remain connected and thus validating the Combo Connect membership.
    button:
      send_terms: Send the term for signature
      resend_terms: Resend term for signature
    resend:
      success:
        title: Resend completed
        description: Term resent successfully
      error:
        title: Resend error
        description: Error resending term. Please try again.
      wait: Please wait... {seconds}s
    catch:
      send_error: >-
        Attention! An error occurred while sending the term. Please try again.
      resend_error: >-
        Attention! An error occurred while resending the term. Please try again.
    steps:
      toast:
        informative:
          not_found:
            title: Biometrics not realized
            description: |-
              The sending of biometrics was not identified.
              Please conclude biometrics to advance from step.
    status:
      waiting_biometry: Awaiting shipping
    footer:
      policy: Access cancellation policy
      resend: Resend
      cancel: Edit project data
      contract: View documents
      terms: View terms
      insurance: Download policy
      view: View
    modal_preview_file:
      title: Download documents - {type}
  guarantor_section_title: Enter the personal data of the guarantor
  customer_section_title: Enter the personal data of the client
  equipment_update:
    alert:
      contract_generated_title: Equipment changes will generate a contractual amendment that must be signed by your customer.
      contract_generated_description: By confirming the change, your customer will receive the term for signature. Once signed, the change will be implemented.
      pending_title: There is a pending change request.
      pending_description: The requested equipment change will be implemented as soon as your customer signs the Financing Amendment Term (TAF).
      pending_edit_title: Equipment change in progress
      pending_edit_description: You started editing the equipment but haven't saved yet. Before continuing, review the information and save or cancel the edit.
      pending_equipment_title: Pending equipment change
      pending_equipment_description: The equipment change you requested is pending your customer's signature. Wait for the signature to continue.
      pending_biometry_timeline_title: Financing Amendment Term pending customer signature
      pending_biometry_timeline_description: Your customer needs to sign the Financing Amendment Term (TAF) to confirm the changes requested in the proposal.
    modal:
      details_title: Equipment change details
      details_description: For the change to be implemented, your customer must sign the Financing Amendment Term (TAF) sent.
      details_subtitle: These were the equipment informed in this change
      details_primary: Resend term to customer
      details_secondary: Cancel change
    button:
      view_details: View details
      resend_term: Resend term
      cancel: Cancel change
    toast:
      resend_success_title: The term was resent to the customer
      resend_success_description: The Financing Amendment Term was resent to your customer for signature.
      cancel_success_title: The change request was canceled
      cancel_success_description: The equipment change you requested has been canceled.
      cancel_error_title: Error canceling change request
      cancel_error_description: An error occurred while canceling the change request. Please try again.
      update_success_title: Change requested successfully
      update_success_without_taf_description: Your equipment was changed successfully.
    tag:
      pending: Pending change request
      mr_pending: Requires step 3 completion
company_form_document:
  label_document_majority_and_legal_representants: >-
    RG or CNH of the majority shareholder and legal representative(s) (if
    applicable)*
  label_energy_bill: Electricity bill for the installation site*
  label_document_company: Company officialization documents*
  label_additional_document: Additional documents
  label_subtitle_document_majority_and_legal_representants: >-
    We accept: RG or CNH or CNH Digital + QR Code or physical CTPS or active
    class card with photo. Documents must contain the CPF number.
  label_subtitle_energy_bill: >-
    Speed up your analysis! Give preference to .pdf files from the utility
    company's website. Send the complete bill, in the name of the individual or
    legal entity, issued within 90 days.
  label_subtitle_document_company: >-
    We accept one of these documents: declaration of individual
    microentrepreneur (MEI), articles of association, application for business
    or, in the absence of these, a certificate issued by the Commercial Board.
  label_subtitle_additional_document: >-
    If the place of establishment is not the address of the CNPJ or the
    residence of the legal representative or co-obligor, it will be necessary to
    send proof of residence in the name of one of these three entities of the
    place of establishment.
  additional_comments: Additional comments
  placeholder: Drag file here or click to upload
  placeholder_additional_comments: Optional
formDocument:
  labelDocumentSol: RG or CNH of client*
  labelElectricityBill: Electricity bill for the installation site*
  labelAdditionalDocument: Additional documents
  labelSubtitleDocument: >-
    We accept: RG or CNH or CNH Digital + QR Code or physical CTPS or active
    class card with photo. Documents must contain the CPF number.
  labelSubtitleElectricityBill: >-
    Speed up your analysis! Preferably .pdf files from the dealership website.
    Send the complete bill, in the name of the individual or legal entity, up to
    90 days from the date of issue.
  labelSubtitleAdditionalDocument: >-
    Not mandatory. Example: proof of connection between the financing client and
    the electricity bill holder.
  additionalComments: Additional comments
  placeholder: Drag file here or click to upload
  placeholderAdditionalComments: Optional
  responseErrorDelete: Error deleting file, please try again.
  responseSuccessDelete: File deleted successfully.
formAddress:
  countryside: Endereço está em Zona Rural?
  yesCountryside: Sim
  noCountryside: Não
  cep: CEP*
  installationStreet: Rua da instalação*
  street: Rua*
  street_placeholder: Rua, alameda, avenida...
  number: Número*
  complement: Complemento
  complementPlaceholder: Apt, bloco...
  neighborhood: Bairro*
  neighborhood_placeholder: Nome do bairro
  state: Estado*
  city: Cidade*
  city_placeholder: Nome do bairro
  local: Local de residência do cliente, é o mesmo local da instalação?
  yesLocal: Local de residência, é o mesmo do local da instalação
  noLocal: Local de residência e instalação não são os mesmo
  titleInstallationLocation: Complete as informações do local de instalação
  addressInstallation: Endereço do local de instalação
solfacilPoints:
  label: Pontos Solfácil Mais
  phrases:
    one: 'How about adding to your financing:'
    two: Let's do the project better? How about adding
    three: Good project! How about adding {string}? Very good!
    four: Formalizing this project will bring you many benefits! Very good!
toastKitSelected:
  title: Selected Kit
  description: >-
    If available at the time of formalization, the kit will be invoiced
    automatically for your convenience.
receiptModel:
  tags:
    inAnalysis: In analysis
    approved: Approved
    denied: Rejected
    inProgress: In progress
    pendingChange: Pending invoice
    selected: Selected
    transferInProgress: Transfer in progress
  alertInstallments:
    title: Receipt model in two payments ({firstValue}% and {secondValue}%)
  response:
    store_order_expired: The selected order has expired.
    error: >-
      An error occurred while trying to update the receipt model. Please try
      again later.
    equipment_value_error: Equipment value must be greater than to financing amounts
    email_error: >-
      An error occurred while trying to update the email. Please try again
      later.
subscriptionEnergy:
  title: Your customer can still have solar energy!
  subtitle: >-
    Refer your customer to have solar energy by subscription with up to 20% off
    on the electricity bill! And earn a commission if they accept!
  recommend_customer: Refer customer
  alt_img: Image Subscription Solar Energy
  title_img: Subscription Solar Energy
project_data:
  supplier_equipment: Supplier equipment invoice*
  status:
    pending: Pending
    selected: Selected
    in_progress: In progress
  equipment_summary:
    title: About the equipment
    distributor: Distributor
    distributor_name: Solfácil
    kit_value: Total kit value
    paid_directly: Amount paid directly to the Solfácil distributor.
    container_helper: Equipment acquired via Container Balance.
  form:
    due_day: Due day
    first_due_date: First installment date
  errors:
    failed_to_load_project: Unable to load project information. Please try again.
    inverter_brand_mismatch: All inverters must be from the same brand.
    string_overload_max: >-
      Risk of overload in the inverters. Increase the inverter power or reduce
      the module power.
    microinverter_overload_max: >-
      Excessive power in the selected microinverters. Increase the quantity or
      choose lower power models.
    microinverter_overload_min: >-
      Insufficient power in the selected microinverters. Reduce the quantity of
      microinverters in the project or choose a higher power model.
    modules_price_per_watt_over: |-
      The cost per Watt ($/WP) needs to be within internal policies.
      Increase the power of the modules to continue
    modules_price_per_watt_under: |-
      The cost per Watt ($/WP) needs to be within internal policies.
      Decrease the power of the modules to continue
    modules_system_power: >-
      The power of the system (sum of the power of the modules) needs to be
      close to that previously filled in the simulation
    battery_capacity: >-
      The total capacity of the batteries needs to be close to that previously
      filled in the simulation
    battery_capacity_over: >-
      The total battery capacity is above the simulated capacity. Decrease the
      battery capacity to continue
    battery_capacity_under: >-
      The total battery capacity is below the simulated capacity. Increase the
      battery capacity to continue
    string_overload_min: |-
      Module power very low in relation to the inverter.
      This can affect system efficiency.
      Consider using a lower power inverter or add more modules to the project.
financing:
  emptyList:
    title: No projects ongoing
    subtitle: Ops! We couldn't find any projects with the filters you applied.
    button_list: New project
  boxSearch:
    placeholder: Search by Name, CPF/CNPJ or ID
  filters:
    clear: |-
      Clear filter |
      Clear filters
    title: 'Filter projects by:'
    steps: Steps
    status: Status
    combo_type:
      label: Combo type
      combo: Easy Combo
      combo_connect: Connect Combo
  quick_filters:
    formalized_90_days: Formalized in the last 90 days
    pending_installations_90_days: Pending installations in the last 90 days
    not_installed: Not installed
    label: Quick filters
  no_data: No data found
  notFound:
    subtitle: >-
      Search for another Name, CPF, CNPJ or project ID to find the project you
      are looking for.
  list:
    expired: Expired
    one_day_remaining: 1 day
    days_remaining: '{days} days'
    step:
      canceled: Canceled
      concluded: Completed
      expired: Expired
      biometry_and_contract: Biometry and contract
      customer_data: Customer data
      equipment: Equipment
      payment_model: Payment model
      proposal: Proposal
      installation: Installation
      simulation: Simulation
    status:
      canceled: Canceled
      expired: Expired
      pending: In progress
      waiting_customer: Waiting for customer
      reproved: Failed
      under_analysis: Under review
      to_review: To review
      approved: Approved
    tags:
      combo: Easy Combo
      combo_connect: Connect Combo
    headers:
      id: ID
      customer_name: Customer name
      financed_value: Financed value
      project_value: Project value
      expires_at: Expires in
      installing_time: Installing time
      installing_time_tooltip: Time elapsed between contract formalization and installation approval, in calendar days.
      step: Step
      deadline_for_formalization: Deadline of the step
      validity_of_the_proposal: Validity of the proposal
  alert:
    title: Here you will only find loans created on the new platform
    subtitle: To access financing from the old platform, click the button on the right.
    action: View old list
    bill:
      description: |-
        Unfortunately, we are still unable to serve this customer.
        However, a new attempt may be made in 30 days.
      title: We were unable to continue with this financing
  ErrorEmptyState:
    title: Oops, something went wrong
    subtitle: |-
      There was a problem and we were unable to bring the result.
      Try again later
installationPage:
  footer:
    formalization: Go to proposal
  canceled:
    title: Project canceled
    description: >-
      This project was canceled and no amount will be transferred. Transfers
      already made will be automatically refunded.
    combo_description: The kit order was also canceled.
  trackAmpera: Track Ampera
  titleAddFiles: Validate your installation with a photo
  descriptionAddFiles: >-
    If you are unable to activate your Ampera, manually validate the project by
    sending the requested documentation below.
  descriptionAvoidFailures:
    part1: Avoid rejections!
    part2: >-
      If the inverter brand has been changed, specify the model/power in the
      additional comments. Maximum file size
    part3: 2MB per file (JPEG, JPG, PNG, and PDF).
  waiting:
    img:
      name: ampera.svg
      alt: Picture of the Ampera equipment
      title: Ampera
    pageTitle: Validate installation with Ampera
    title: Waiting for Ampera activation
    subtitle: >-
      As soon as you install the Ampera, your installation will be automatically
      validated. [strong]If more than 15 minutes have passed since you installed
      the Ampera and it has not been activated,[/strong] proceed with the
      inverter validation.
inversorInstallation:
  form:
    title: Validate installation with inverter
    taf_pending_tooltip: Wait for the Financing Amendment Term (TAF) to be signed before submitting installation information.
  InversorInstallationConfirmation:
    title: Confirm if the information below is correct with the installed inverter
    confirmButton: Confirm data
    reviewButton: Review data
unauthorized:
  title: Unauthorized
  description: >-
    You do not have permission to access this page. If you believe this is an
    error, please contact the system administrator.
notFound:
  title: Page not found
  description: >-
    The page you are looking for does not exist. If you believe this is an
    error, please contact the system administrator.
unknonwError:
  title: Without information's
  description: >-
    An error occurred while processing your request. If you believe this is an
    error, please contact the system administrator.
noResults:
  title: No results found
  description: >-
    We couldn't find any results for your search. Try again with different
    terms.
emptyStateCustomer:
  title: No information available
emptyStateRegister:
  subtitle: |-
    There was a problem obtaining financing information.
    Try reloading the page.
formalization:
  upload:
    maximum_size:
      text: Tamanho máximo de
      10mb: 10MB
    accepted_files:
      text: por arquivo
      files: (JPEG, JPG, PNG e PDF)
      files_rg_cnh: (JPEG, JPG, PNG e PDF)
      files_cnh_digital: (PDF)
    cpf_attention:
      text: Atenção!
      description: Documentos devem conter o número do CPF
  client:
    cpf: CPF do(a) cliente
    name:
      label: Nome Completo do(a) cliente
      placeholder: Preencha o nome completo do cliente
      placeholder_guarantor: Fill in the full name of the guarantor
      label_guarantor: Full name of the guarantor
    birthdate: Data de Nascimento do cliente
    phone: Celular do(a) cliente
    email:
      label: E-mail do(a) cliente
      placeholder: Insira o email do cliente
      label_guarantor: Email from the guarantor
      placeholder_guarantor: Enter the Email of the guarantor
  customer_documents:
    document:
      example:
        title: Exemplo de como deve ser enviado
        image:
          front:
            alt: Exemplo de RG frente
            title: Frente do documento
          back:
            alt: Exemplo de RG verso
            title: Verso do documento
  electricity_bill:
    title: Conta de luz do local da instalação
    info_bill: >-
      Envie a conta completa, em nome da pessoa física ou jurídica de até 90
      dias de emissão.
    info_files: >-
      Agilize sua análise! De preferência para arquivos em .pdf do site da
      concessionária. Tamanho máximo de 10MB por arquivo (JPEG, JPG, PNG e PDF)
    form:
      electricity_bill_customer_name: A conta de luz está no nome do seu cliente?
      proof_link_document: Comprovante de vínculo
      document_account_holder:
        label: CPF do titular da conta
        placeholder: Insira o CPF do titular da conta
    what_is_proof_of_link: O que é comprovante de vínculo?
    energy_concessionaire: Energy concessionaire
    concessionaire_placeholder: Select
    access_opinion:
      label_optional: Access opinion (optional)
      label_required: Access opinion (required)
      optional_title: This project may need an Access Opinion
      optional_description: >-
        If the sum of the inverters' power exceeds 7.5 kW, {concessionaire} may require
        the Access Opinion (also known as Preliminary Budget), a technical document that
        authorizes connecting the solar system to the grid. This check is the integrator's
        responsibility. If that is the case, attach the document below.
      optional_title_fallback: This project may need an Access Opinion
      optional_description_fallback: >-
        If the sum of the inverters' power exceeds 7.5 kW, the energy utility may require
        the Access Opinion (also known as Preliminary Budget), a technical document that
        authorizes connecting the solar system to the grid. This check is the integrator's
        responsibility. If that is the case, attach the document below.
      required_title: '{concessionaire} requires the Access Opinion for this project'
      required_description: >-
        Also known as Preliminary Budget, the Access Opinion is a technical document issued
        by the energy utility that authorizes connecting the solar system to the grid.
        Request it directly from {concessionaire} and attach it below to continue.
      required_title_fallback: The energy utility requires the Access Opinion for this project
      required_description_fallback: >-
        Also known as Preliminary Budget, the Access Opinion is a technical document issued
        by the energy utility that authorizes connecting the solar system to the grid.
        Request it directly from the energy utility and attach it below to continue.
      required_error: Attach the Access Opinion to continue.
      loading: Checking whether the Access Opinion is required
  other_beneficiary_units:
    question: Will other units be beneficiaries/consumers of this Solar project?
  electricity_bills:
    additional_unit_bill: Additional unit electricity bill
    form:
      electricity_bill_customer_name: Is the electricity bill in your customer's name?
      proof_link_document: Proof of connection
      document_account_holder:
        label: CPF of the account holder
        placeholder: Enter the CPF of the account holder
    what_is_proof_of_link: What is proof of connection?
    bill_number: 'Unit {number}'
    remove_bill: Remove unit
    add_beneficiary_unit: + Add another beneficiary/consumer unit
    modal_what_is_bond:
      description:
        part_1: Document proving the
        bond_degree: 1st-degree relationship
        part_2: between the
        holder: electricity bill holder
        part_3: and the
        borrower: credit borrower
      cards:
        first:
          title: Child, parent or sibling as the electricity bill holder
          text_start: Attach the
          emphasis: ID or driver's license (with CPF)
          text_end: ' of the account holder.'
        second:
          title: Spouse or partner as the electricity bill holder
          text_start: Attach a
          emphasis: Marriage or Stable Union Certificate
          text_end: >-
            '. The names of the account holder and the credit borrower must
            appear as parties in the document.'
      okay: Got it!
  address:
    guarantor:
      label: Address of the guarantor
price:
  modal:
    title: Price and Fees Update
    secondary: I don't want to edit simulation
    primary: I understood and I want to continue
    body: >-
      Our rates and prices have changed since the last simulation you have made
      for this project
    subbody: >-
      Your simulation will only be updated if you confirm the editions
      performed. Otherwise, you will follow the same amount initially recorded.
global:
  statuses:
    in_progress: In progress
    under_analysis: Under analysis
    approved: Approved
    to_review: To review
    waiting_customer: Waiting for customer
    reproved: Reproved
    expired: Expired
    canceled: Canceled
toast:
  approved:
    documentation:
      title: Approved documentation
      description: >-
        We have analyzed the submitted documentation and everything is correct!
        Proceed to the next step.
  incomplete:
    documentation:
      title: Incomplete documentation
      description: Check if all fields are filled in to send the analysis.
  error:
    upload:
      title: Error sending file
      description: >-
        An error occurred while sending the file, check the file type or size
        and try again.
    send:
      title: Error sending
      description: >-
        An error occurred while sending the form. Check the fields and try
        again.
    reproved:
      title: Reproved documentation
      description: |-
        Documentation sent does not comply with the required requirements.
        Make a new simulation or contact us.
    edit:
      biometry:
        send:
          description: |-
            Unfortunately we could not process your request at this time.
            Please try again later!
  sended:
    documentation:
      title: Documentation sent
      description: Documentation sent successfully!
  success:
    send:
      description: |-
        We analyze the documentation sent and everything is correct!
        Follow to the equipment stage.
  informative:
    send:
      under_analisys:
        title: Documentation in Analysis with Specialist
        description: >-
          Our expert is analyzing the documentation sent and will return within
          24 hours.
payment-model:
  modal-confirmation:
    cancel: Back to Edition
    send: I'm aware and I want to continue
    title: Attention! Check out the selected options
    description-1: >-
      After sending the invoices for analysis, the options selected in the
      receiving model
    description-2: >-
      Be sure to check if you are selected the best way to receive your
      business! Do you want to continue?
    description-alert: cannot be edited
    equipment-warning: >-
      After submitting your Receipt Model, it will no longer be possible to change the equipment in your project.
    vkit-warning: >-
      Integrator partner, your financing has a value lower than the contracted kit. You will need to disburse the remaining amount to the distributor.
receipt-model:
  load_error:
    not_found:
      action: Click to reload
    unprocessable:
      title: Could not load the receipt model
      subtitle: >-
        Something unexpected happened and the receipt model could not be loaded.
        Please try again.
      action: Try again
  send-billing-letter:
    error:
      title: Unvised billing letter, please make sure the emails are correct.
      description: Check the selected emails and try again.
    success:
      title: Billing letter sent!
      description: The billing letter was sent to added emails.
  invoice:
    project_invoice_alert:
      title: How the project invoice should be
      text: >-
        Issue a single invoice for the <strong>total financed amount</strong> —
        equal to the equipment cost plus the services value. The
        <strong>payer</strong> must be the end customer financing the project
        (not Solfácil).
  model_select:
    service:
      split: '{cash}% upfront + {installation}% after installation approval'
      after_installation: 100% after installation approval
      in_cash: 100% upfront
      unknown: According to model {code}
    column_model: Model
    column_supplier: Kit supplier
    column_service: Service
    supplier_in_cash: Upfront
    aria_label: 'Receipt model: {model} — {service}'
    listbox_label: Available receipt models
  supplier_select:
    label: Distributor*
    placeholder: Select...
    search_placeholder: Search distributor...
    search_label: Search distributor
    listbox_label: Distributors
    group_partners: Partner distributors
    group_others: Other authorized distributors
    no_results: No results found
    loading: Loading distributors...
  email_tags:
    required: Add at least one email before sending.
  receivables:
    title: Amounts to receive
    details_title: Financing details
    groups:
      kit_invoice: After kit invoice approval
      receipt_approval: After receipt details approval
      installation: After installation is completed
    transfer_title: Transfer {number}
    descriptions:
      services_installment: 'Services rendered {index}/{total} ({percent}%)'
      services_single: Single installment, 100% of the invoice amount
      commission_financing: Financing commission
      commission_insurance: Insurance commission
      kit_reimbursement: Kit purchase reimbursement
    status:
      analysis: Under review
      pending: Pending
      scheduled: Scheduled
      paid: Paid
      error: Error
      cancelled: Cancelled
    in_analysis: Under review
    view_receipt: View receipt
    view_receipt_aria: View receipt for {title}
    total: Total to receive
    empty:
      title: Awaiting invoices
      subtitle: Attach and submit your invoices so the amounts to receive can be verified.
    empty_confirm:
      title: Confirm to see the amounts
      subtitle: >-
        The amounts to receive show up here after you confirm the receipt
        model.
    no_values:
      title: No amounts to receive
      subtitle: This project has no amounts pending receipt.
    details:
      financed_value: Total financed amount
      equipment: Equipment
      kit: Kit from distributor {supplier}
      kit_reimbursable: Kit from distributor {supplier} (Reimbursable)
      kit_unnamed: Distributor kit
      kit_reimbursable_unnamed: Distributor kit (Reimbursable)
      my_services: My services
      my_commissions_and_services: My commissions and services
      services: Services rendered
      commission_financing: Financing commission
      commission_insurance: Insurance commission
  layout:
    model_section:
      title: Receipt model
      helper: >-
        You can choose among the receipt models that were available to your
        company when the contract was signed.
    invoice_denied_alert:
      title: The invoice you sent contains incorrect information that prevents its analysis
      fix_text: >-
        To fix it, check the invoice data and issue an {correctionLetter} on
        your state's SEFAZ portal or in the NF-e issuing system, without having
        to cancel the invoice.
      correction_letter: Electronic Correction Letter (CC-e)
      attach_text: 'After issuing it, download the PDF, {attachLink} and send it for analysis again.'
      attach_link: attach it in the field below
    invoice_divergent_alert:
      title: Invoice with information that differs from the project
      text: >-
        Check that the kit description, address, fiduciary lien and receipts
        are filled in correctly on the invoice you sent. If needed, attach a
        correction letter with the information adjusted to match the project.
    approved_alert:
      title: Documentation approved! Follow your payments in Amounts to receive
      text: >-
        The documentation you sent was approved by our specialists. In Amounts to
        receive you can follow the transfers and the payments made by Solfácil.
    resend_for_analysis: Resend for analysis
    missing_payout_option: Select a receipt model before sending.
  supplier_info:
    title: About the equipment
    kit_value_label: Total kit value
    vkit_paid:
      title: Kit payment completed successfully.
      text: The payment was made directly to the distributor.
      downloading: Downloading receipt...
    vkit_scheduled:
      title: Kit payment scheduled.
      text: The payment will be made directly to the distributor.
    partner_alert:
      title: The partnership between Solfácil and {name} brings you advantages.
      text: >-
        As a partner distributor, you won't need to pay for the kit upfront or
        compromise your cash flow. We will validate the order and pay the
        distributor directly.
    not_partner_alert:
      title: With this distributor, you pay for the kit and we reimburse you
      text: >-
        Since this distributor does not invoice Solfácil directly, you need to
        pay for the kit yourself.
      text_complement: After that, attach the purchase invoice below so we can reimburse you.
  billing_letter:
    errors:
      invalid_email: Invalid email
      send_failed: Enter a valid email
    title: Billing letter
    description: >-
      Document issued by Solfácil authorizing the partner distributor to issue
      the invoice for the financed kit.
    description_complement: Enter the distributor's contact emails below to send it.
    emails_label: Contact email(s) of the distributor {name}
    send: Send emails
    resend: Resend
    download: Download the letter
    recipients: 'To:'
    sent_alert:
      title: Billing letter sent.
      text: If needed, you can resend the letter using the button below.
    next_steps:
      title: 'After sending the billing letter:'
      contact_before: 'Contact the distributor '
      contact_after: .
      informed_before: 'Let them know you have already sent the Solfácil '
      informed_bold: billing letter
      informed_after: .
      request_before: 'Request the '
      request_bold: kit invoice
      request_after: .
      attach_bold: Attach below
      attach_after: ' the invoice you received.'
  invoices:
    errors:
      delete_failed: File not deleted, please try again!
    title: Invoices
    products_label: Equipment invoice*
    commission_label: Financing commission invoice*
    commission_hint: Use the information below to issue your invoice.
    reminder:
      title: Remember to send the information for review at the bottom of the page.
      title_to_review: Remember to resend the information for review at the bottom of the page.
      text_single: The invoice has been attached. Now just send it and wait for the review.
      text_plural: The invoices have been attached. Now just send them and wait for the review.
      text_to_review: Attach the Correction Letter, resend and wait for the review.
  commission_info:
    address_neighborhood: District {neighborhood}
    address_zipcode: 'ZIP: {zipcode}'
    title: Information for issuing the commission invoice
    value: Invoice amount
    cnpj: CNPJ
    corporate_name: Corporate name
    municipal_registration: Municipal registration (IM)
    main_activity: Main activity (CNAE)
    address: Address
    email: Email
result:
  tag:
    guarantor:
      pending: Need a guarantor
      approved: Approved guarantor
      partially_approved: Partial
      total_approved: Approved
  bnpl:
    reproved:
      description: Unfortunately, this condition is not available to your customer.
    description_card: >-
      Get your solar kit in the Solfácil store, and offer this new condition as
      another possibility of closing deals!
defaultDenialReason: Validates if the file is correct
accordion:
  date:
    completedAt: Completed on
    orderChangedAt: Order changed on
    reprovedAt: Disapproved on
    submittedAt: Submitted on
    in_progress: (in progress)
blocked_list:
  alert:
    title: Your customer is on the blocked list and cannot be financed.
    description: Please contact us for more information.
    action: Contact us
  whatsapp:
    message: Hello, I would like to know more about the blocked list.
project:
  max_installments: Term
  shortage: Deficiency
  entry: Prohibited
  down_payment: Minimal input
  kit_acquisition: Acquisition Kit
  solfacil_store: Solfácil Store
  solfacil_store_or_external: Solfácil or External Store
  solfacil_store_with_asterisk: Solfácil Store*
reassessment:
  modal:
    title: We approve new customers in your portfolio
    button: Go to reassessment
    seeLater: See later
    neverShowAgain: Never show again
    description:
      prefix: 'With '
      highlight: reassessment
      suffix: >-
        you don't lose the opportunity to make a deal! We reanalyze the credit
        of customers with rejected projects and approve special conditions for
        you.
  alert:
    title: New customers approved in your portfolio
    description: >-
      With Reassessment SolFácil you don't lose the opportunity to make a deal!
      We reanalyze the credit of customers with rejected projects and approve
      special conditions for you.
  notFound:
    title: You don't have approved customers in the reassessment
    subtitle: >-
      This is an automatic process and you will be notified by the platform when
      you have a project reanalyzed.
    emptyFilter: Try adjusting the filters to find the desired projects
blocked:
  default_installation_delay_alert:
    title: >-
      We identified uninstalled and delinquent financings, and because of this your
      access to new financings may be suspended.
    description: >-
      To avoid suspension, you must complete the pending installations or guide
      clients to regularize overdue payments.
    action: View open installations
  default_installation_delay_temporary:
    title: >-
      We identified uninstalled and delinquent financings, suspending
      temporarily your access to new financings.
    description: >-
      To normalize your access, you must complete the pending installations or guide
      clients to regularize overdue payments.
    action: View open installations
  risk_buyback_alert:
    title: >-
      We identified uninstalled and delinquent financings, and because of this your
      access to new financings may be suspended.
    description: >-
      To avoid suspension, you must complete the pending installations or guide
      clients to regularize overdue payments.
    action: View open installations
  risk_buyback_temporary:
    title: >-
      We identified uninstalled and delinquent financings, suspending
      temporarily your access to new financings.
    description: >-
      To normalize your access, you must complete the pending installations or guide
      clients to regularize overdue payments.
    action: View open installations
  high_default_rate_temporary:
    title: >-
      Your access to new financings is temporarily blocked due to the high volume of
      delinquency in your portfolio.
    description: >-
      Check delinquent clients and guide them to regularize pending amounts to
      restore your access.
    action: View pending clients
  high_default_rate_alert:
    title: >-
      Your access to new financings may be temporarily blocked due to the high
      volume of delinquency in your portfolio.
    description: >-
      Check delinquent clients and guide them to regularize overdue installments to
      avoid possible blocks.
    action: View pending clients
  preventive_temporary:
    title: >-
      Your access to new financings is temporarily blocked and under internal
      review.
    description: >-
      Please wait while we analyze your registration. If this time has
      already passed, contact an account manager through the phone +55 (11) 91238-6374
      or directly via WhatsApp by clicking on the button.
    action: Talk to a manager
  preventive_alert:
    title: Your access to new financings may be blocked.
    description: >-
      To understand the reasons and regularize your situation, contact an account
      manager through the phone +55 (11) 91238-6374 or directly via WhatsApp by clicking
      on the button.
    action: Talk to a manager
  pending_receipt_model_alert:
    title: >-
      Your access to new financings may be blocked!
    description: >-
      We identified projects with the receipt model stage open for more than 7 days.
      Complete within 6 days to avoid block
      Check the open projects and complete this stage now
    action: Regularize projects
  pending_receipt_model_temporary:
    title: >-
      Your access to new financings is temporarily blocked because we identified
      projects with the receipt model stage open for more than 14 days.
    description: >-
      Check the open projects and complete this stage on the platform to resume your
      simulations with Solfácil.
    action: Regularize projects
  limit_temporary:
    title: >-
      You still have uninstalled projects that compromised your current credit
      limit, preventing new financings.
    description: >-
      To resume new formalizations, you must complete the pending installations.
    action: Conclude installations
  partner_inactive_temporary:
    title: >-
      Your access to new financings is temporarily blocked.
    description: >-
      Since more than 90 days have passed without transactions, your access has been
      temporarily blocked. To regularize your situation, contact an account manager
      through the phone +55 (11) 91238-6374 or directly via WhatsApp by clicking
      on the button.
    action: Talk to a manager
  confirmed_fraud_definitive:
    title: Your access to new financings is permanently blocked due to internal policies.
    description: ''
    action: ''
  blacklist_legal_webjud_definitive:
    title: >-
      Your access to new financings is permanently blocked due to internal policies.
    description: ''
    action: ''
  partnership_termination_definitive:
    title: >-
      Your access to new financings is permanently blocked due to internal policies.
    description: ''
    action: ''
  payment_block_bots_amr_alert:
    title: >-
      Attention!
    description: >-
      There are values to be returned from cancellations of clients that we have
      already paid to you. Please contact our Central or your GC for more details.
    action: Talk to a manager
  solarinove_integration_alert:
    title: >-
      Attention!
    description: >-
      Complete your company registration to enable project formalization.
    action: Finish registration
  deteriorated_financial_health_temporary:
    title: >-
      Your access to new financings is temporarily blocked and under internal
      review.
    description: >-
      Please wait while we analyze your registration. If this time has
      already passed, contact an account manager through the phone +55 (11) 91238-6374
      or directly via WhatsApp by clicking on the button.
    action: Talk to a manager
  deteriorated_financial_health_alert:
    title: >-
      Your access to new financings is temporarily blocked and under internal
      review.
    description: >-
      Please wait while we analyze your registration. If this time has
      already passed, contact an account manager through the phone +55 (11) 91238-6374
      or directly via WhatsApp by clicking on the button.
    action: Talk to a manager
  missing_operational_agreement_temporary:
    title: >-
      Your access to new financings is temporarily blocked
    description: >-
      To regularize, the administrator user needs to sign the Operational Agreement
      on the Solfácil platform.
    action:
  generic:
    title: >-
      You have some temporary block!
    description: >-
      Contact an account manager to regularize your situation. +55 (11) 91238-6374
      or directly via WhatsApp by clicking on the button.
    action: Talk to a manager
biometry:
  loading:
    messages: >-
      We are almost there, wait while we process the biometry, this may take a
      few seconds, As soon as it is available you will be able to continue with
      the financing With Reassessment Solfácil you don't lose the opportunity to
      make a deal! We reanalyze the credit of customers with rejected projects
      and approve special conditions for you.
  button:
    try_again: Try again
  notFound:
    title: You don't have approved customers in the reassessment
    subtitle: >-
      This is an automatic process and you will be notified by the platform when
      you have a project reanalyzed.
    emptyFilter: Try adjusting the filters to find the desired projects
modal:
  multiAccountAutoConsumption:
    title: Remote self-consumption
    actionButton: Okay, I understand!
    description: >-
      Remote self-consumption allows using energy generated by a solar plant in multiple
      electricity bills, as long as they are all under the same CPF or CNPJ. This modality
      is guaranteed by Law No. 14,300/2022, the Legal Framework for Distributed Generation.
    attentionTitle: Attention, Integrator
    attentionMessage: >-
      Before formalizing, guide the customer about the rules of remote self-consumption and
      the need to concentrate electricity bills in the generating CPF/CNPJ, so that all units
      are benefited by energy generation.

onboarding:
  common:
    skip_all: Skip all
    previous: Previous
    next: Next
    finish: Finish
  layout:
    header:
      step1:
        title: Smart navigation and search
        description: Improved navigation and search always available, in any page of the Financing Portal.
      finish_home: See more updates
      finish_internal: Finish
      step2:
        title: Smart search
        description: Now you have a smart search always fixed and accessible on any page.
      step3:
        title: Navigation menu
        description: Navigate between the main areas using the tab menu.
  pages:
    overview:
      step1:
        title: Start new simulation
        description: Here you start simulations and financing contracts quickly.
      step2:
        title: Projects in progress panel
        description: >-
          Follow your financing pipeline, from simulations to installations.
          By clicking on the status, you will go directly to the filtered list of those projects.
      step3:
        title: Benefits and Offers
        description: Access exclusive benefits and offers available for you and your clients.
      step4:
        title: Wallet Health
        description: Follow your client portfolio health and see how to improve your financing rates.
      step5:
        title: Payment Model
        description: See your current payment model and your performance. For the details, go to My wallet.
fileUpload:
  existingFile: File uploaded
  loading: Loading file...
  replace: Replace
  view: View
  download: Download
  emptyLabel: Click to upload or drag and drop the file
  helperText: '{exts} (max. {maxSizeMb}MB)'
  errors:
    maxSize: File must be at most {maxSizeMb}MB.
    invalidFormat: 'Invalid format. Accepted: {formats}'
  preview:
    title: PDF preview
    loadError: Could not load the PDF
components:
  email_tags_input:
    placeholder: Enter the email(s)
    helper: >-
      Type an email and press comma or ENTER to add it. Repeat to include more
      recipients.
    invalid_email: Enter a valid email.
    max_reached: 'Limit of {max} emails reached. Remove one to add another.'
    remove_aria: Remove {email}
    list_aria: Added emails
my_wallet:
  title: My wallet
  tabs:
    aria: My wallet tabs
    summary: Summary
    receiving_model: Payment model
  updated_at: 'Updated on {date} at {time}'
  list_and: '{items} and {last}'
  list_separator: ', '
  loading: Loading wallet information
  not_impersonate:
    title: View a partner's wallet
    description: To do this, enter partner viewing mode.
    button: View with partner
  error:
    title: We couldn't load this information
    description: Something went wrong while fetching your wallet data. Please try again in a few moments.
    retry: Reload
  forbidden:
    title: You don't have access to this information
    description: Ask your account administrator for access to the company's wallet.
  empty:
    title: There is no wallet information yet
    description: The data shows up here as soon as it is processed. Please check back later.
  block_error:
    title: We couldn't load this information.
    retry: Reload
  status_banner:
    contact: contact us
    enabled:
      title: Enabled for new financing
      description: All good to finance new projects. Keep your wallet health indicators up to date.
      description_starting: You are just getting started. Your indicators will be calculated as you move forward with your projects.
    warning:
      title: Stay alert to avoid being blocked
      description: 'You have one or more indicators {highlight} that need attention.'
      highlight: on alert
    blocked:
      title: Blocked for new financing
      description: 'There are items at a {highlight} in your wallet that need to be resolved.'
      highlight: critical level
    blocked_policy:
      title: Account blocked due to non-compliance with internal policies.
      description: 'New financing, payment models and credit limit are unavailable. For more information, {contact}.'
    blocked_financial_health:
      title: Financing for new projects is not available for your company.
      description: 'After a review of your company''s financial health, financing for new projects had to be blocked indefinitely{br}For more information, {contact}.'
  no_completions:
    value: No performance to evaluate
    caption: 'in the last {days} days'
    title: 'You have not completed any installation in the last {days} days.'
    description: 'To choose your model, we look at the installations completed in this period. With none, you get paid through Model {model}.'
  models:
    tag: 'MODEL {model}'
    split: '{upfront}% / {after}%'
    split_caption: At the start / At the end
    titles:
      C: 'Get it all{br}at the end'
      B: 'Half at the start,{br}half at the end'
      A: 'Get almost all{br}at the start'
    chips:
      DEFAULT: Default
      ENABLED: Enabled
      DISABLED: Disabled
      BLOCKED: Blocked
      WAIVED: Commercial exception
      LOCKED: Model not reached yet
  receiving_model:
    title: Payment model
    how_it_works: How does it work?
    see_performance: See my performance
    cards_aria: Payment models
    essential_banner:
      alt: Concentrate your purchases and financing at Solfácil. The more projects you complete, the more advantages you get.
    carousel:
      go_to: 'Go to card {index} of {total}'
    commercial_exception_banner:
      title: Commercial exception active.
      description: You can use all payment models.
    delinquency_banner:
      text: 'Model {model} is disabled because {highlight} is on alert.'
      highlight: your wallet's delinquency
    card:
      requirements_label: What you need
      for_whom_label: Who it is for
      steps_waived_note: You don't need to meet the items below. Solfácil has unlocked this model for your company.
      conditions:
        REGISTRATION_ENABLED:
          requirement:
            text: 'Have your {highlight} for financing.'
            highlight: registration enabled
          for_whom:
            text: 'Any integrator with an {highlight} can use it.'
            highlight: enabled registration
        FIRST_INSTALLATIONS_MANDATORY:
          text: 'Your {highlight} are always paid through this model.'
          highlight: 'first {value} installations'
        ONLY_MODEL_BELOW_THRESHOLD:
          text: 'Have {highlight} in the last {days} days.'
          highlight: 'less than {value}% of installations completed on time'
        fallback: Default model condition.
      essential_condition:
        text: 'Companies on the {highlight} can only use this model.'
        highlight: Essential Profile
      novice_next:
        text: 'After them, we check whether you {highlight} {onTime}.'
        highlight: complete your installations
        on_time: on time
      requirements:
        REGISTRATION_ENABLED_FOR_FINANCING:
          text: 'Have your {highlight} for financing.'
          highlight: registration enabled
        MIN_COMPLETED_INSTALLATIONS:
          text: 'Have {highlight} completed and approved in the wallet.'
          highlight: more than 5 installations
        DELINQUENCY_UNDER_CONTROL:
          text: 'Keep {highlight} under control.'
          highlight: delinquency levels
        ON_TIME_PERCENTAGE_RANGE:
          label: 'Installations completed in the last {days} days:'
          text: '{range} {onTime}'
          on_time: on time.
          ranges:
            B: Between 50% and 89%
            A: Above 90%
        fallback: Model requirement.
      requirement_status_label: '{status}:'
      requirement_status:
        done: Met
        pending: Pending
        alert: On alert
        waived: Not applicable
      milestone_status: 'You have done: {status}'
      milestone_value: '{completed} of {total}'
      current_performance: 'You currently have: {value}'
      performance_value: '{percentage}% on time'
      performance_link_context: '(Model {model}, see in My performance)'
      not_evaluated: not measured yet
      unavailable: Unavailable
    performance:
      title: My performance
      subtitle: The more installations you complete on time, the better the way you get paid.
      not_evaluated_banner:
        title: Right now, your deadlines don't count.
        description: Even so, see below which model you would have without the commercial exception.
      total_label: Total
      total_value: '{count} installation completed | {count} installations completed'
      total_caption: 'in the {window}'
      window: 'last {days} days'
      current_label: Current performance
      current_value: '{percentage}% on time'
      enables_one: 'Enables only Model {models}'
      enables_many: 'Enables Models {models}'
      enables_none: '-'
      enabled_models: '{models}.'
      requires_delinquency_mark: '{model}*'
      challenge_label: Your goal
      challenge_value: 'Complete {count} more on time'
      challenge_caption: 'to reach Model {model}.'
      keep_value: '*Keep delinquency under control'
      keep_caption: to stay on Model A.
      commercial_label: Commercial exception
      commercial_value: 'Model {models} is also enabled | Models {models} are also enabled'
      commercial_caption: The Solfácil sales team unlocked it for you
      blocked_title: All models are disabled
      blocked_reason: 'Reason: your company cannot take on new financing'
      disabled_title: 'Model {model} disabled'
      disabled_reason: 'Reason: {reason}'
      disabled_reasons:
        DELINQUENCY_WARNING: wallet delinquency on alert
        fallback: a model requirement is on alert
      bar:
        label: 'MODEL {model}'
        locked: locked
        waived: unlocked by commercial exception
        mark: '{value}%'
        aria: '{percentage}% of installations completed on time in the last {days} days'
        aria_not_evaluated: Performance not measured yet
        aria_with_states: '{summary}. {states}.'
        state_aria: 'Model {model} {state}'
    novice:
      subtitle: To use Models A and B, first complete your first 5 installations.
      subtitle_done: You have completed your first 5 installations. From the 6th on, we check whether you complete them on time.
      progress_label: First installations
      progress_value: '{completed} of {total} done'
      challenge_label: Your goal
      challenge_label_done: Your next goal
      challenge_value: 'Complete your first {total} installations'
      challenge_value_done: Complete 1 more installation to start counting the deadline
      alert_title: Your first 5 installations are paid through Model C.
      alert_description: From the 6th on, we check whether you complete them on time. That way, you can move to a better model.
      stepper_aria: '{completed} of {total} first installations done'
    completion:
      tabs_aria: Installations completed on time and late
      tabs:
        on_time: On time
        late: Late
      on_time:
        title: Installations completed on time
        description: You completed these installations on time. They help you get a better model.
        status: on time
        kit_prefix: completed within
        sla: 'up to {days} days'
      late:
        title: Installations completed late
        description: You completed these installations after the deadline. They lower your result.
        status: late
        kit_prefix: completed within
        sla: 'more than {days} days'
      count: '{count} of {total} installations'
      caption: 'completed {status}'
      caption_window: 'in the last {days} days'
      see_projects: See installations
      kits:
        SOLFACIL_COMBO: SOLFÁCIL COMBOS
        OTHER_DISTRIBUTOR: KITS FROM OTHER COMPANIES
      kit_count: '{count} installation | {count} installations'
      counted_from:
        SOLFACIL_COMBO: after the kit arrived
        OTHER_DISTRIBUTOR: after signing the contract
      combo_banner:
        title: It's easier with a Solfácil Combo!
        text: 'The deadline only starts when the kit arrives. You have {highlight} and get the best models.'
        highlight: '{days} days to complete the installation'
      gauge_aria:
        on_time: '{count} of {total} installations completed on time'
        late: '{count} of {total} installations completed late'
  summary:
    title: My wallet summary
    card:
      title: Payment model
      best_model: Your best current model
      model_button_prefix: 'See details of Model {model}:'
      performance_label: Current performance
      performance_value: '{percentage}% of installations were completed on time'
      performance_caption: 'in the last {days} days'
      novice_label: First installations
      novice_value: '{completed} of {total} done'
      novice_alert_title: Complete your first 5 installations
      novice_alert_title_done: Complete 1 more installation
      novice_alert_description: From the 6th on, we check whether you complete them on time. That way, you can move to a better model.
      commercial_title: Commercial exception active.
      commercial_description: You can use all payment models.
      disabled_title: 'Model {model} is disabled'
      disabled_reason: 'Reason: {reason}'
      keep:
        text: '{highlight} to stay on Model A.'
        highlight: Keep delinquency under control
      next:
        text: '{highlight} to reach Model {model}.'
        highlight: 'Complete {count} more installation on time | Complete {count} more installations on time'
      blocked_title: All models are disabled
      blocked_description: You can still see your performance.
      see_details: See details
  faq:
    title: Questions about payment models
    search_placeholder: Search the FAQ
    clear: Clear
    results: 'Showing {shown} of {total} questions.'
    show_all: Show all
    empty_title: No questions found
    empty_text: Try other words or clear the search to see all questions.
    items:
      what_are_models:
        question: What is a payment model?
        answer:
          - paragraph: 'It is the way you receive the money for a financed project. There are 3 models:'
          - bullets:
              - '**Model C**: you get everything (100%) at the end of the installation.'
              - '**Model B**: you get half (50%) at the start and half (50%) at the end.'
              - '**Model A**: you get almost everything (90%) at the start and the rest (10%) at the end.'
      everyone_starts_at_c:
        question: Does every integrator start on Model C?
        answer:
          - paragraph: Yes. Model C is the default model. Every integrator with an enabled registration starts on it. Your first 5 installations are always paid through Model C, even if you complete all of them on time.
      how_to_be_eligible_ab:
        question: How do I use Models A and B?
        answer:
          - numbered:
              - Complete your first 5 installations.
              - Complete 1 more installation. From the 6th on, Solfácil starts checking whether you complete them on time.
              - The more installations on time, the better your model.
      how_is_percentage_calculated:
        question: How do you calculate the deadline?
        answer:
          - paragraph: 'We look at the installations you completed in the **last 90 days** (about 3 months). Then we check how many were completed **on time**. Every day, a new day enters the count and the oldest day leaves. The deadline depends on the kit:'
          - bullets:
              - '**Solfácil Combo**: up to 45 days after the kit arrives.'
              - '**Kit from another company**: up to 45 days after signing the financing contract.'
      model_ranges:
        question: How much do I need on time for each model?
        answer:
          - bullets:
              - 'Less than 50% on time → **Model C**'
              - 'Between 50% and 89% on time → **Model B**'
              - '90% or more on time → **Model A**. Model A also requires delinquency under control (see the next question).'
      why_only_a_requires_delinquency:
        question: Why does only Model A require delinquency under control?
        answer:
          - paragraph: On Model A, Solfácil pays almost everything (90%) right at the start. That is a bigger risk. That is why Model A also requires your wallet's delinquency to be under control. Models B and C don't require it.
      can_i_lose_model:
        question: Can I lose a model I already have?
        answer:
          - paragraph: Yes. The calculation always looks at the last 90 days. If you delay more installations, you may drop to another model. On Model A, you can also drop if your wallet's delinquency gets worse.
      when_is_performance_evaluated:
        question: When does my deadline start counting?
        answer:
          - paragraph: 'At the 6th completed installation. The first 5 are paid through Model C and do not enter the deadline count. Meanwhile, the screen shows "Not measured yet".'
      why_still_model_c:
        question: I have completed the first 5. Why am I still on Model C?
        answer:
          - paragraph: Completing the first 5 does not change your model on its own. They only unlock the start of the count. The count starts at the 6th completed installation. Until then, you stay on Model C.
      delinquency_warning:
        question: What happens if my wallet's delinquency goes on alert?
        answer:
          - paragraph: Model A becomes disabled, even if you complete your installations on time. Models B and C keep working normally. When delinquency is back under control, Model A comes back on its own. You don't need to ask.
      delinquency_critical:
        question: What if delinquency reaches a critical level?
        answer:
          - paragraph: Then your company is blocked from new financing. All models become disabled, even Model C. This also happens in other cases, such as fraud, many delayed installations or pending issues. The block is only lifted when everything is resolved.
      why_all_blocked:
        question: Why are all my models blocked?
        answer:
          - paragraph: 'Because your company is blocked from new financing. Some reasons:'
          - bullets:
              - Delinquency at a critical level.
              - Many installations with long delays.
              - Documents or contracts with pending issues.
              - A Solfácil decision after a review.
          - paragraph: Meanwhile, no model is enabled. Contact Solfácil support to learn more.
      essential_profile_models:
        question: Which models can the Essential Profile use?
        answer:
          - paragraph: Only Model C, which is the default. You get everything (100%) at the end of the installation.
      what_is_commercial_exception:
        question: What is the commercial exception?
        answer:
          - paragraph: It is a special condition granted by the Solfácil sales team. It enables all models for your company, even if your deadlines are not enough yet. When it ends, your model depends on your deadlines again.
      what_counts_as_on_time:
        question: What is the deadline to complete the installation?
        answer:
          - paragraph: 'It depends on the kit:'
          - bullets:
              - '**Solfácil Combo**: up to 45 days after the kit arrives.'
              - '**Kit from another company**: up to 45 days after signing the financing contract.'
          - paragraph: Every day counts, including Saturdays, Sundays and holidays.
      why_different_deadlines:
        question: Why is the Solfácil Combo deadline different from a kit from another company?
        answer:
          - paragraph: With the Solfácil Combo, the deadline starts when the kit arrives. The material is already with you. With a kit from another company, the deadline starts when you sign the contract. After that, you still need to buy the material and wait for it to arrive. That is why the deadline is longer.
      do_kits_count_together:
        question: Do installations with a Solfácil Combo and with a kit from another company count together?
        answer:
          - paragraph: 'Yes, they count together. The calculation uses all installations completed in the last 90 days. In the "My performance" section, you see each type separately.'
      data_update_frequency:
        question: When are the numbers updated?
        answer:
          - paragraph: Once a day. The numbers show what happened up to the previous day. The date and time of the last update are at the top of the screen.
